import { AssetSubClassCode } from '@api/api/entity-constants';
import { Entity } from '@api/api/entity-schema';
import { RangeHorizon, ResampleIntervals } from '@chart';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { About } from '@terminal/modules/about/About';
import { OverviewEventFeed } from '@terminal/modules/overview-event-feed/OverviewEventFeed';
import { OverviewFinancials } from '@terminal/modules/overview-financials/OverviewFinancials';
import { ThemeKnowledgeGraph } from '@terminal/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '@terminal/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { SimpleChart } from '@terminal/modules/simple-chart/SimpleChart';
import { useEarningsInsightsByEntities } from '@terminal/react-query-hooks/earnings/useEarningsInsightsByEntities/useEarningsInsightsByEntities';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ChartSearchParamsAssetValue } from '@terminal/utils/chart-search-utils/chart-search-utils';
import { ChartState } from '@terminal/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import clsx from 'clsx';

import { BrandsAndProductsSection } from '../knowledge-graph-widget/components/brands-and-products-section/BrandsAndProductsSection';
import { CompetitorsSection } from '../knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/competitors-section/CompetitorsSection';
import { EarningsInsightSection } from '../knowledge-graph-widget/components/earnings-insight-section/EarningsInsightSection';
import { GeographicExposureSection } from '../knowledge-graph-widget/components/geographic-exposure-tab/geographic-exposure-section/geographic-exposure-section/GeographicExposureSection';
import { UpgradeToUnlock } from './components/upgrade-to-unlock/UpgradeToUnlock';
import styles from './LegacyOverview.module.scss';

export interface LegacyOverviewProps {
  entity: Entity;
}

/**
 * @deprecated Replaced by `Overview` when AO Pulse is enabled. Retained for
 * basic users
 */
export const LegacyOverview = ({ entity }: LegacyOverviewProps) => {
  const chartNavigate = useChartNavigate();

  const { byEntityTag, isLoading: isEarningsLoading } =
    useEarningsInsightsByEntities([entity.tag]);
  const {
    latestEarningsData,
    earningsInsight,
    isPreview = false,
  } = byEntityTag.get(entity.tag) ?? {};

  const isBasicUser = useSubscription(state => state.isBasicUser());

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneMinute,
    horizon: RangeHorizon.OneDay,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate([entityValue], {
      resample: chartState.resample,
      horizon: chartState.horizon,
      seriesType: chartState.chartPanes[0]?.seriesType,
    });
  };

  const displayConnections = isStock;
  const hasEarningsSection = Boolean(latestEarningsData);

  return (
    <>
      <div className={styles.overviewRoot}>
        <SimpleChart
          entity={entity}
          key={entity.tag}
          initialChartSettings={chartSettings}
          isLegacyVariant={false}
          onClickFullscreen={navigateToChart}
          maxHorizon={RangeHorizon.TenYears}
          eventsConfig={{
            TMI: true,
          }}
          source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
        />
        <div className={styles.sectionsGrid}>
          <div
            className={clsx(styles.eventFeedCell, {
              [styles.eventFeedCellSingleRow]: !hasEarningsSection,
            })}
          >
            {isBasicUser ? (
              <div className={styles.upgradeToUnlockWrapper}>
                <UpgradeToUnlock />
              </div>
            ) : (
              <ErrorBoundaryWrapper>
                <OverviewEventFeed
                  entityTag={entity.tag}
                  source={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
                />
              </ErrorBoundaryWrapper>
            )}
          </div>
          {hasEarningsSection && (
            <div className={styles.earningsCell}>
              <ErrorBoundaryWrapper>
                <EarningsInsightSection
                  entity={entity}
                  earningsData={latestEarningsData}
                  earningsInsight={earningsInsight}
                  isLoading={isEarningsLoading}
                  isPreview={isPreview}
                />
              </ErrorBoundaryWrapper>
            </div>
          )}
          <div
            className={clsx(styles.aboutCell, {
              [styles.aboutCellTopRow]: !hasEarningsSection,
            })}
          >
            <About
              entity={entity}
              variant="secondary"
              showMoreDescription
              limit={600}
            />
          </div>
          {displayConnections && (
            <>
              <div className={clsx(styles.gridCell, styles.themeCell)}>
                <ErrorBoundaryWrapper>
                  <ThemeKnowledgeGraph entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.competitorsCell)}>
                <ErrorBoundaryWrapper>
                  <CompetitorsSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.geographicCell)}>
                <ErrorBoundaryWrapper>
                  <GeographicExposureSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
              <div className={clsx(styles.gridCell, styles.brandsCell)}>
                <ErrorBoundaryWrapper>
                  <BrandsAndProductsSection entityTag={entity.tag} />
                </ErrorBoundaryWrapper>
              </div>
            </>
          )}
        </div>
        {isStock && (
          <>
            <OverviewFinancials entity={entity} />
            <OverviewValuation entity={entity} />
          </>
        )}
      </div>
    </>
  );
};
